import { Info } from '@mui/icons-material';
import { CompanyDocumentResell } from 'lib/api/reSell/factoring/types';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import { AutocompleteOption } from 'lib/common/GlobalSearcher/filterTypes';
import WarningDeleteDialog from 'lib/common/WarningDeleteDialog';
import { DocumentProfileContext } from 'lib/context/DocumentProfileProvider';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination, {
  Header,
} from 'lib/designSystem/TableWithPagination';
import { formatDate, validatePermissions } from 'lib/functions/utils';
import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import MultiBuyBack from './components/MultiBuyBack';
import MultiPaymentFundResell from './components/MultiPayment';
import MultiResellMain from './components/NewMultiResell';
import RegisterSelector from './components/RegisterSelector';
import ResellDetails from './components/ResellDetails';
import RevertResell from './components/RevertResell';
import { FactoringResellProvider, useFactoringResell } from './context';

const statusOptions = [
  { label: 'Todas', value: 'ALL' },
  { label: 'Completada', value: 'COMPLETED' },
  { label: 'Activa', value: 'ACTIVE' },
  { label: 'Recomprada', value: 'REBUYED' },
];

const FactoringResellsContent: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const { onOpenDocumentProfile } = useContext(DocumentProfileContext);

  const {
    // Datos
    resells,
    pagination,
    selectedResellIds,
    loading,

    // Setters
    setResells,
    setSelectedResellIds,
    setPagination,
    setLoading,

    // Estados de UI
    openNewResell,
    openMultiSales,
    openMultiPayment,
    setOpenMultiPayment,
    openMultiBuyBack,
    openRegisterSelector,
    setOpenRegisterSelector,
    openRevert,
    setOpenRevert,
    resellToRevert,
    openFundPendingReviewDialog,
    setOpenFundPendingReviewDialog,
    fundPendingReviewMessages,

    // Filtros
    filters,
    setBusinessIdentifier,
    setStakeholderIdentifier,
    setFolio,
    setFundId,
    setSelectedFund,
    setStatus,
    setStartCreatedAt,
    setEndCreatedAt,
    setStartDueDate,
    setEndDueDate,
    setSelectedSortBy,
    setSortOrder,

    // Acciones
    fetchResellings,
    triggerSearch,
    handleDetails,

    // Constantes
    PAGE_SIZE,
  } = useFactoringResell();

  function handleOpenDocument(id: string) {
    const row = resells.find((x) => x.id === Number(id));
    if (!row) return;
    onOpenDocumentProfile(row.documentId);
  }

  const headers: Header[] = [
    { key: 'id', label: 'Id', type: 'numeric-id' },
    {
      key: 'folio',
      label: 'Folio',
      handleAction: handleOpenDocument,
      icon: <Info fontSize="small" />,
    },
    { key: 'businessName', label: 'Cliente' },
    { key: 'businessIdentifier', label: 'Rut cliente' },
    { key: 'stakeholderName', label: 'Deudor' },
    { key: 'fundName', label: 'Fondo' },
    { key: 'amount', label: 'Monto', type: 'money' },
    {
      key: 'createdAt',
      label: 'Creación',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    {
      key: 'dueDate',
      label: 'Vencimiento',
      type: 'date',
      formatter: (value: Date) => formatDate(value, 'dd-mm-yyyy'),
    },
    { key: 'cessionDate', label: 'Cedido', type: 'confirm-lights' },
    { key: 'status', label: 'Estado', type: 'status-chip' },
  ];

  const searchFilters: Filter[] = [
    {
      type: 'text',
      label: 'Folio',
      value: filters.folio,
      setter: setFolio,
    },
    {
      type: 'text',
      label: 'Rut cliente',
      value: filters.businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      type: 'text',
      label: 'Rut deudor',
      value: filters.stakeholderIdentifier,
      setter: setStakeholderIdentifier,
    },
    {
      type: 'select',
      label: 'Estado',
      value: filters.status,
      setter: setStatus,
      options: statusOptions,
    },
    {
      type: 'autocomplete',
      label: 'Fondo',
      value: filters.fundId,
      key: 'fundName',
      setter: (value: string | null) => {
        setFundId(value);
      },
      selectedValue: filters.selectedFund,
      setSelectedValue: (value: AutocompleteOption | null) => {
        setSelectedFund(value);
        if (value?.object?.id) {
          setFundId(value.object.id);
        } else {
          setFundId(null);
        }
      },
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: filters.startCreatedAt,
      setter: setStartCreatedAt,
      dateKey: 'startCreatedAt',
      dateType: 'created',
      pairKey: 'endCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de creación',
      value: filters.endCreatedAt,
      setter: setEndCreatedAt,
      dateKey: 'endCreatedAt',
      dateType: 'created',
      pairKey: 'startCreatedAt',
    },
    {
      type: 'date',
      label: 'Fecha de vencimiento',
      value: filters.startDueDate,
      setter: setStartDueDate,
      dateKey: 'startDueDate',
      dateType: 'due',
      pairKey: 'endDueDate',
    },
    {
      type: 'date',
      label: 'Fecha de vencimiento',
      value: filters.endDueDate,
      setter: setEndDueDate,
      dateKey: 'endDueDate',
      dateType: 'due',
      pairKey: 'startDueDate',
    },
  ];

  const buttonsData: CustomButtonProps[] = [
    {
      name: 'Buscar',
      color: 'primary',
      action: () => {},
      customButton: (
        <GlobalSearcher
          filters={searchFilters}
          triggerSearch={() => triggerSearch()}
          selectedSortBy={filters.selectedSortBy}
          setSelectedSortBy={setSelectedSortBy}
          sortOptions={[
            { value: 'createdAt', label: 'Creación' },
            { value: 'dueDate', label: 'Vencimiento' },
          ]}
          sortOrder={filters.sortOrder}
          setSortOrder={setSortOrder}
          dispatch={dispatch}
        />
      ),
    },
    {
      action: () => setOpenRegisterSelector(true),
      name: 'Registrar',
      color: 'primary',
      disabled:
        !validatePermissions(user, 'financing_fundSale_new') &&
        !validatePermissions(user, 'financing_fundSale_new_payment'),
    },
  ];

  const cantSelectRow = (row: CompanyDocumentResell) => {
    return row.status === 'Completada' || row.status === 'Recomprada';
  };

  useEffect(() => {
    void fetchResellings({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las ventas a fondos."
        variant="body1"
        buttons={buttonsData}
      />
      <TableWithPagination
        selected={selectedResellIds}
        setSelected={setSelectedResellIds}
        cantSelectRow={cantSelectRow}
        dataHeaders={headers}
        data={resells}
        setData={setResells}
        rowsPerPageDefault={50}
        handleClickRow={handleDetails}
        exportToFile="xlsx"
        exportComplete
        searchData={fetchResellings}
        pagination={pagination}
        setPagination={setPagination}
        loading={loading}
        setLoading={setLoading}
        lastElement
      />
      {openNewResell && <ResellDetails />}
      {openMultiSales && <MultiResellMain />}
      {openMultiPayment && <MultiPaymentFundResell />}
      {openMultiBuyBack && <MultiBuyBack />}
      {openRegisterSelector && <RegisterSelector />}
      {openRevert && (
        <RevertResell
          open={openRevert}
          setOpen={setOpenRevert}
          resellToRevert={resellToRevert}
          triggerSearch={triggerSearch}
        />
      )}
      {openFundPendingReviewDialog && (
        <WarningDeleteDialog
          open={openFundPendingReviewDialog}
          setOpen={(value: boolean) => {
            setOpenFundPendingReviewDialog(value);
            if (!value) {
              setOpenMultiPayment(false);
            }
          }}
          title="Pagos registrados correctamente"
          warningMessages={fundPendingReviewMessages}
          deleteItem={async () => ({ displayMessage: '' })}
          requireConfirmation={false}
          mode="info"
          severity="success"
        />
      )}
    </div>
  );
};

const FactoringResells: React.FC = () => {
  return (
    <FactoringResellProvider>
      <FactoringResellsContent />
    </FactoringResellProvider>
  );
};

export default FactoringResells;
